import { getItineraryForLocations } from '../BookingWidget/LocationSelector/locationUtils';
import { AirCalendarData, AirCalendarPrice, TravelClass, WidgetTab } from '../common';
import { EnvConfig } from '../config';
import { SelectionState } from '../store';
import { Indexed, isFunction, isNotIn, isPresent, sanitize } from '../utils';
import { httpRequest } from './httpUtils';
import { CastResponse, castResponse, Lock, Response } from './types';

const offersLock: Lock = new Lock();
const airCalendarLock: Lock = new Lock();
export const NO_FLIGHTS_STATUSES = ['NO_FLIGHTS_FOUND', 'NO_FLIGHTS_LEFT_FOR_TODAY'];

// == Interfaces ===============================================================

type Cabin = 'business' | 'ecopremium' | 'eco' | 'mixed';

interface StatusResponse {
  status: string;
}

interface AirCalendarResponse {
  status: string;
  currency?: string;
  airCalendars: any[];
}

interface AirBoundsItinerary {
  departureLocationCode: string;
  destinationLocationCode: string;
  departureDate: string;
  directFlights?: boolean;
  isRequestedBound: boolean;
}

interface AirBoundsBackendParams {
  locale: string;
  travelers: {
    adults: number;
    children: number;
    c15s: number;
    infants: number;
  };
  cabin: string;
  itineraries: AirBoundsItinerary[];
  selectedBoundId?: string;
  promoCode?: string;
}

// == Utilities ================================================================

const mapTravelClassToCabin = (travelClass: TravelClass): Cabin => {
  switch (travelClass) {
    case TravelClass.ECONOMY:
      return 'eco';
    case TravelClass.PREMIUM_ECONOMY:
      return 'ecopremium';
    case TravelClass.BUSINESS:
      return 'business';
    case TravelClass.MIXED:
      return 'mixed';
    default:
      return 'eco';
  }
};

const flattenSelections = (selections: SelectionState): Record<any, any> => {
  const {
    locations,
    travelDates,
    paxAmount,
    travelClass,
    directFlights,
    promoCode,
    activatedTab,
    locale,
    travelType,
    isCountryShopTestGroup,
  } = selections;

  return sanitize({
    locale,
    itinerary: getItineraryForLocations(locations, travelDates, travelType),
    adults: paxAmount.adults,
    c15s: paxAmount.c15s,
    children: paxAmount.children,
    infants: paxAmount.infants,
    cabin: travelClass.toUpperCase(),
    directFlights,
    promoCode: activatedTab !== WidgetTab.AWARD ? promoCode : undefined,
    isAward: activatedTab === WidgetTab.AWARD,
    isCountryShopTestGroup,
  });
};

const httpHeaders = (loginToken: string): Indexed<string> => {
  const baseHeaders: Indexed<string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  return isPresent(loginToken) ? { ...baseHeaders, Authorization: `Bearer ${btoa(loginToken)}` } : baseHeaders;
};

const retrieveOffers = (selection: SelectionState, offersUrl: string, loginToken: string): Promise<Response<any>> => {
  const params = flattenSelections(selection);

  return httpRequest({
    method: 'POST',
    url: offersUrl,
    body: JSON.stringify(params),
    headers: httpHeaders(loginToken),
    retries: 2,
    lock: offersLock,
  });
};

const retrieveBounds = (
  { locations, locale, paxAmount, travelClass, travelDates, directFlights, promoCode, travelType }: SelectionState,
  airBoundsUrl: string,
  loginToken: string
): Promise<Response<any>> => {
  const { adults, c15s, children, infants } = paxAmount;
  const itineraries: AirBoundsItinerary[] = getItineraryForLocations(locations, travelDates, travelType).map(
    (itinerary, i) => {
      return {
        ...itinerary,
        isRequestedBound: i === 0,
        directFlights,
      };
    }
  );

  const body: AirBoundsBackendParams = {
    locale,
    cabin: mapTravelClassToCabin(travelClass),
    travelers: {
      adults,
      children,
      c15s,
      infants,
    },
    itineraries,
    promoCode: promoCode || undefined,
  };

  const headers: Indexed<string> = {
    ...httpHeaders(loginToken),
    ...{
      'Content-Type': 'application/json',
    },
  };

  return httpRequest({
    method: 'POST',
    url: airBoundsUrl,
    body: JSON.stringify(body),
    headers,
    retries: 2,
    lock: offersLock,
  });
};

// == Services =================================================================

export const getFlightAvailability = (
  { airBoundsUrl, offersUrl }: EnvConfig,
  selection: SelectionState,
  fpp: boolean,
  getLoginToken?: () => string
): Promise<Response<boolean>> => {
  cancelFlightAvailability();
  const loginToken: string = isFunction(getLoginToken) ? getLoginToken() : undefined;
  const resp: Promise<Response<any>> = fpp
    ? retrieveBounds(selection, airBoundsUrl, loginToken)
    : retrieveOffers(selection, offersUrl, loginToken);
  return resp
    .then(({ status, payload }: Response<StatusResponse>) => ({
      status,
      payload: isNotIn(payload.status, NO_FLIGHTS_STATUSES),
    }))
    .catch(castResponse as CastResponse<boolean>)
    .finally(() => offersLock.release());
};

export const cancelFlightAvailability = (): boolean => offersLock.cancel();

export const getAirCalendar = (
  { airCalendarUrl }: EnvConfig,
  selection: SelectionState,
  getLoginToken?: () => string
): Promise<Response<AirCalendarData>> => {
  cancelAirCalendar();
  const loginToken: string = isFunction(getLoginToken) ? getLoginToken() : undefined;
  const params = flattenSelections(selection);

  return httpRequest({
    method: 'POST',
    url: airCalendarUrl,
    body: JSON.stringify(params),
    headers: httpHeaders(loginToken),
    retries: 2,
    lock: airCalendarLock,
  })
    .then(({ status, payload: { currency, airCalendars } }: Response<AirCalendarResponse>) => {
      /* eslint-disable indent */
      const prices: AirCalendarPrice[] = isPresent(airCalendars)
        ? airCalendars.map(({ arrival, departure, totalPrice, totalPoints, originalPrice, pricePerAdult }: any) => ({
            departureDate: departure,
            returnDate: arrival,
            totalPrice,
            totalPoints,
            currency,
            originalPrice,
            pricePerAdult,
          }))
        : [];
      return { status, payload: { prices } };
      /* eslint-enable indent */
    })
    .catch(castResponse as CastResponse<AirCalendarData>)
    .finally(() => airCalendarLock.release());
};

export const cancelAirCalendar = (): boolean => airCalendarLock.cancel();
